/* eslint-disable react-refresh/only-export-components */
/**
 * Performance Context and Calculation Logic
 * Centralized performance state management and calculation
 */

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { performanceMonitor } from '@/lib/utils/performanceMonitor';
import { cacheService } from '@/lib/utils/cacheService';

interface PerformanceMetrics {
  responseTime: number;
  cacheHitRate: number;
  slowQueries: number;
  totalOperations: number;
  healthScore: number;
}

interface PerformanceContextType {
  metrics: PerformanceMetrics;
  isMonitoring: boolean;
  startMonitoring: () => void;
  stopMonitoring: () => void;
  calculateHealthScore: (metrics: Omit<PerformanceMetrics, 'healthScore'>) => number;
}

const PerformanceContext = createContext<PerformanceContextType | undefined>(undefined);

interface PerformanceProviderProps {
  children: ReactNode;
}

export function PerformanceProvider({ children }: PerformanceProviderProps) {
  const [metrics, setMetrics] = useState<PerformanceMetrics>({
    responseTime: 0,
    cacheHitRate: 100,
    slowQueries: 0,
    totalOperations: 0,
    healthScore: 100
  });
  const [isMonitoring, setIsMonitoring] = useState(false);

  // Calculate health score based on performance metrics
  const calculateHealthScore = useCallback((
    metrics: Omit<PerformanceMetrics, 'healthScore'>
  ): number => {
    let score = 100;
    
    // Response time penalty (0-50 points)
    if (metrics.responseTime > 1000) score -= 50;
    else if (metrics.responseTime > 500) score -= 25;
    else if (metrics.responseTime > 200) score -= 10;
    
    // Cache hit rate bonus/penalty (±20 points)
    const cacheBonus = (metrics.cacheHitRate - 80) * 0.4;
    score += Math.max(-20, Math.min(20, cacheBonus));
    
    // Slow queries penalty (0-30 points)
    score -= Math.min(30, metrics.slowQueries * 5);
    
    return Math.max(0, Math.min(100, score));
  }, []);

  // Monitor performance when enabled
  useEffect(() => {
    if (!isMonitoring) return;

    const interval = setInterval(() => {
      // Get current performance data
      const perfSummary = performanceMonitor.getSummary();
      const dbSummary = typeof performanceMonitor.getDBSummary === 'function'
        ? performanceMonitor.getDBSummary()
        : {};
      const cacheStats = cacheService.getStats();
      const slowQueries = performanceMonitor.getSlowQueriesReport();
      
      // Calculate average response time
      const avgResponseTime = Object.values(dbSummary).reduce((sum, table) => 
        sum + (table.avg || 0), 0) / Math.max(Object.values(dbSummary).length, 1);
      
      // Calculate total operations
      const totalOps = Object.values(perfSummary).reduce((sum, op: unknown) => 
        sum + (op.count || 0), 0);
      
      // Calculate cache hit rate
      const cacheHitRate = cacheStats.totalEntries > 0 
        ? ((cacheStats.totalEntries - cacheStats.expiredEntries) / cacheStats.totalEntries) * 100
        : 100;
      
      const newMetrics = {
        responseTime: avgResponseTime,
        cacheHitRate,
        slowQueries: slowQueries.length,
        totalOperations: totalOps,
        healthScore: 0 // Will be calculated below
      };
      
      // Calculate health score
      newMetrics.healthScore = calculateHealthScore(newMetrics);
      
      setMetrics(newMetrics);
    }, 1000); // Update every second while monitoring

    return () => clearInterval(interval);
  }, [isMonitoring, calculateHealthScore]);

  const startMonitoring = () => {
    setIsMonitoring(true);
    console.log('[Performance] Monitoring started');
  };

  const stopMonitoring = () => {
    setIsMonitoring(false);
    console.log('[Performance] Monitoring stopped');
  };

  const value: PerformanceContextType = {
    metrics,
    isMonitoring,
    startMonitoring,
    stopMonitoring,
    calculateHealthScore
  };

  return (
    <PerformanceContext.Provider value={value}>
      {children}
    </PerformanceContext.Provider>
  );
}

export function usePerformance() {
  const context = useContext(PerformanceContext);
  if (context === undefined) {
    throw new Error('usePerformance must be used within a PerformanceProvider');
  }
  return context;
}

// Hook for performance monitoring with auto-start/stop
export function usePerformanceMonitoring() {
  const { metrics, isMonitoring, startMonitoring, stopMonitoring } = usePerformance();
  
  const start = () => {
    if (!isMonitoring) {
      startMonitoring();
    }
  };
  
  const stop = () => {
    if (isMonitoring) {
      stopMonitoring();
    }
  };
  
  return {
    metrics,
    isMonitoring,
    startMonitoring: start,
    stopMonitoring: stop,
    isLoading: isMonitoring
  };
}